import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SignupAddress, SignupAddressLookup } from '../address/signup-address.store';
import { TcpaBrowserMetadata } from './tcpa-browser-metadata.service';

export const CUSTOMER_INTAKE_ENDPOINT = '/customer-intake';
export const ADDRESS_LOOKUP_ENDPOINT = `${CUSTOMER_INTAKE_ENDPOINT}/lookup`;
export const INTAKE_SUBMIT_ENDPOINT = `${CUSTOMER_INTAKE_ENDPOINT}/submit`;

/** `TcpaConsentSubmissionDto`: the consent text shown, verbatim, plus when and where it was accepted. */
export interface CustomerIntakeTcpa {
  browserMetadata: TcpaBrowserMetadata;
  consentedTo: string;
  timestamp: string;
}

/**
 * The contact fields of `CreateCustomerIntakeDto` (property-tax-backend `PUT /customer-intake`), shared by both
 * saves below. Local until it's published in `@kin/property-tax-types`.
 */
export interface CustomerIntakeRequest {
  firstName: string;
  lastName: string;
  email: string;
  /** E.164, e.g. "+15553334444". */
  phone: string;
  taxAcctId?: string;
  address: SignupAddress;
}

/** The enrollment's save and its corrections: no TCPA consent, which it sends once with `submit`. */
export interface EnrollmentIntakeRequest extends CustomerIntakeRequest {
  /** Checks Form 50-162's signer capacity; left for the signer to check when omitted. */
  ownerRelationship?: IntakeOwnerRelationship;
}

/**
 * The "Is this you?" answers that can enroll: the owner (`self`) or a co-owner on the deed (`co`) signs as the
 * property owner (`propertyOwnerCheck`); a trustee or LLC manager (`entity`) signs as another person authorized
 * to act for the owner (`otherPersonCheck`).
 */
export type IntakeOwnerRelationship = 'self' | 'co' | 'entity';

/** `IntakeSigningDto`: the embedded Dropbox Sign form a new customer's intake started. */
export interface IntakeSigning {
  signatureRequestId: string;
  /** Embedded signing URL to open with the Dropbox Sign client. */
  signingUrl: string;
  clientId: string;
  documentName: string;
}

/** `CustomerIntakeResponseDto`: the intake row plus the county match (with owner details) for its address. */
export interface CustomerIntakeResponse extends Omit<SignupAddressLookup, 'token'> {
  id: string;
  userId: string;
  addressId: string;
  taxAcctId: string | null;
  createdAt: string;
  /**
   * The signing form for a new customer in a county we serve. Null when the address didn't match, the email
   * already has an account (`requiresLogin`), or signing couldn't be started.
   */
  signing: IntakeSigning | null;
  /** True when the email already belongs to an account; the customer has to log in to continue. */
  requiresLogin: boolean;
  /**
   * Indicative annual home insurance premium (RC1) for the address, in dollars: an estimate, not a bindable quote.
   * Null when the address didn't match, the email already has an account, Kindex has no estimate for the address or
   * scores it below the backend's threshold, or Kindex couldn't be reached.
   */
  rc1Estimate?: number | null;
}

/** The submit body: the TCPA consent is recorded once, with the submit that makes the intake final. */
export interface SubmitCustomerIntakeRequest {
  tcpa: CustomerIntakeTcpa;
}

/** `SubmitCustomerIntakeResponseDto`. */
export interface SubmitCustomerIntakeResponse {
  id: string;
  submittedAt: string;
}

@Injectable({ providedIn: 'root' })
export class CustomerIntakeService {
  private http = inject(HttpClient);
  private url = `${environment.apiUrl}${CUSTOMER_INTAKE_ENDPOINT}`;
  private lookupUrl = `${environment.apiUrl}${ADDRESS_LOOKUP_ENDPOINT}`;
  private submitUrl = `${environment.apiUrl}${INTAKE_SUBMIT_ENDPOINT}`;

  /**
   * County coverage + avgSavings for an address, run before sign-up. The backend saves an empty intake for it
   * and returns its `token`, which `submit` sends back. Body is the address itself (`CustomerIntakeAddressDto`).
   *
   * @todo - unit matching: the backend matches units exactly, so a unit-numbered building looked up
   *   without its unit (or with a different format) comes back unmatched (→ waitlist).
   */
  lookup(address: SignupAddress): Observable<SignupAddressLookup> {
    return this.http.post<SignupAddressLookup>(this.lookupUrl, address);
  }

  /**
   * Saves the enrollment on the intake a lookup made (identified by its `token`): records the user and address
   * (the TCPA consent comes with `submit`). Until `submit`, it can be saved again to correct it, which regenerates
   * the signing form when the details changed. 401: the token is invalid or expired · 409: already submitted, or
   * the form already signed · 429: too many saves (→ /too-many-requests; see signupThrottleInterceptor).
   */
  saveEnrollment(request: EnrollmentIntakeRequest, token: string): Observable<CustomerIntakeResponse> {
    return this.http.put<CustomerIntakeResponse>(this.url, request, { headers: bearer(token) });
  }

  /**
   * Saves the waitlist's intake ahead of its `submit` (the TCPA consent comes with `submit`, as for enrollment).
   * 401: the token is invalid or expired · 409: already submitted · 429: too many saves.
   */
  saveWaitlist(request: CustomerIntakeRequest, token: string): Observable<CustomerIntakeResponse> {
    return this.http.put<CustomerIntakeResponse>(this.url, request, { headers: bearer(token) });
  }

  /**
   * Submits the form, once, with the TCPA consent: the last save becomes final. 409: never saved, or already
   * submitted.
   */
  submit(request: SubmitCustomerIntakeRequest, token: string): Observable<SubmitCustomerIntakeResponse> {
    return this.http.post<SubmitCustomerIntakeResponse>(this.submitUrl, request, { headers: bearer(token) });
  }
}

function bearer(token: string) {
  return { Authorization: `Bearer ${token}` };
}

/** "(214) 216-6542" → "+12142166542" (US numbers only). */
export function toE164(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  return digits ? `+1${digits.slice(-10)}` : '';
}
